/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * AI scores for a set of leads or deals, and the actions that produce them.
 *
 *   const ai = useAiRecordScores("leads", ids);
 *   ai.enabled      this user may use the AI Assistant (module + seat + credits):
 *                   show scores and "Score with AI"; otherwise nothing AI shows
 *   ai.canScore     same as enabled; kept for callers that gate the action
 *   ai.scores       { [recordId]: { score, band, reasons, scored_at, stale } }
 *   ai.scoreOne(id) re-score one record now
 *   ai.scoreMany(ids, { onProgress }) bulk score, at most BATCH_MAX ids
 *
 * The backend scores a bulk run in chunks and stops before the HTTP timeout,
 * handing back `remaining`; scoreMany keeps sending that until the run is done
 * or stops for credits. Unchanged records are skipped server-side, so sending
 * a record twice costs nothing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import useAiAssistantStatus from "hooks/useAiAssistantStatus";
import { getApiErrorMessage } from "utils/apiErrors";

export const BATCH_MAX = 200;
const READ_CHUNK = 500;

export default function useAiRecordScores(objectApiName, ids) {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const { canUseAiAssistant, refresh: refreshStatus } = useAiAssistantStatus();
  const enabled = Boolean(!entitlementsLoading && isModuleEnabled("AI_ASSISTANT") && canUseAiAssistant);
  const ready = Boolean(enabled && isAuthReady && isAuthenticated && axiosInstance);
  const [scores, setScores] = useState({});
  const [scoring, setScoring] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const idKey = useMemo(
    () =>
      Array.from(new Set((ids || []).map(Number).filter(Boolean)))
        .sort((a, b) => a - b)
        .join(","),
    [ids]
  );

  const merge = useCallback((list) => {
    if (!mounted.current || !list?.length) return;
    setScores((prev) => {
      const next = { ...prev };
      list.forEach((s) => {
        next[s.record_id] = s;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!ready || !idKey) return undefined;
    let cancelled = false;
    const all = idKey.split(",");
    (async () => {
      for (let i = 0; i < all.length && !cancelled; i += READ_CHUNK) {
        try {
          const res = await axiosInstance.get(`/ai/scores/${objectApiName}`, {
            params: { ids: all.slice(i, i + READ_CHUNK).join(",") },
          });
          if (!cancelled) merge(res.data?.scores || []);
        } catch (err) {
          // Scores are an extra; a failed read just leaves the column empty.
          return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, idKey, objectApiName, axiosInstance, merge]);

  const handleError = useCallback(
    (err) => {
      if ([402, 403].includes(err?.response?.status)) refreshStatus();
      return getApiErrorMessage(err, "The AI Assistant could not score this. Please try again.");
    },
    [refreshStatus]
  );

  const scoreOne = useCallback(
    async (recordId) => {
      setScoring(true);
      try {
        const res = await axiosInstance.post(`/ai/scores/${objectApiName}/${recordId}`);
        merge([{ ...res.data, stale: false }]);
        return { ok: true, score: res.data };
      } catch (err) {
        return { ok: false, message: handleError(err) };
      } finally {
        if (mounted.current) setScoring(false);
      }
    },
    [axiosInstance, objectApiName, merge, handleError]
  );

  const scoreMany = useCallback(
    async (recordIds, { onProgress } = {}) => {
      const wanted = Array.from(new Set(recordIds.map(Number))).slice(0, BATCH_MAX);
      const summary = { scored: 0, skipped: 0, failed: 0, notFound: 0, message: null };
      let pending = wanted;
      setScoring(true);
      try {
        while (pending.length) {
          const res = await axiosInstance.post(`/ai/scores/${objectApiName}/batch`, { ids: pending });
          const data = res.data || {};
          merge((data.scores || []).map((s) => ({ ...s, stale: false })));
          summary.scored += (data.scores || []).length - (data.skipped || []).length;
          summary.skipped += (data.skipped || []).length;
          summary.failed += (data.failed || []).length;
          summary.notFound += (data.not_found || []).length;
          if (onProgress) onProgress(wanted.length - (data.remaining || []).length, wanted.length);
          const remaining = data.remaining || [];
          if (data.stopped && data.stopped !== "time") {
            summary.message = data.message || "Scoring stopped before every record was scored.";
            if (data.stopped === "out_of_credits") refreshStatus();
            break;
          }
          // No progress means the next call would do the same; stop instead of looping.
          if (remaining.length >= pending.length) break;
          pending = remaining;
        }
      } catch (err) {
        summary.message = handleError(err);
      } finally {
        if (mounted.current) setScoring(false);
      }
      return summary;
    },
    [axiosInstance, objectApiName, merge, handleError, refreshStatus]
  );

  const canScore = enabled;
  // Stable identity: list pages memoize table rows on this, and react-table
  // resets paging and sorting whenever its data array changes.
  return useMemo(
    () => ({ enabled, canScore, scores, scoring, scoreOne, scoreMany }),
    [enabled, canScore, scores, scoring, scoreOne, scoreMany]
  );
}
